import React, { FC, useContext } from "react";
import { ErrorBoundary } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { Link, Text } from "@xometry/xometry_loft";
import Logger from "./src/utils/logger";
import {
  UserValidation,
  UserValidationContext,
} from "./src/utils/contexts/user-validation-context";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactElement | (() => React.ReactElement);
}

/**
 * Application-level Error Boundary that catches all unhandled errors and provides
 * comprehensive logging.
 *
 * Features:
 * - Automatic Sentry logging with user context
 * - Logger utility integration for Coralogix preparation
 * - User-facing error logging
 * - Structured error data with rich context
 */
const XomErrorBoundary: FC<Props> = (props) => {
  const { children, fallback } = props;
  const user = useContext(UserValidationContext);
  // Cache session ID to avoid repeated storage access (performance optimization)
  const sessionIdRef = React.useRef<string | null>(null);

  const getSessionId = (): string => {
    if (!sessionIdRef.current) {
      sessionIdRef.current =
        sessionStorage.getItem("sessionId") ||
        localStorage.getItem("sessionId") ||
        `session_${Date.now()}`;
    }
    return sessionIdRef.current;
  };

  const generateErrorId = (): string => {
    // Use crypto.randomUUID if available, fallback to timestamp + random
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `app_err_${crypto.randomUUID()}`;
    }
    return `app_err_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 11)}`;
  };

  // Async error handling to prevent blocking (performance fix)
  const handleError = (
    error: unknown,
    componentStack: string | undefined,
    eventId: string
  ) => {
    const errorId = generateErrorId();
    const sessionId = getSessionId();

    // Create rich error context for logging
    const errorContext = {
      errorId,
      boundaryLevel: "app",
      sessionId,
      componentStack,
      eventId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    // Non-blocking async error logging
    Promise.allSettled([
      // Sentry logging with context
      new Promise<void>((resolve) => {
        Sentry.withScope((scope) => {
          scope.setTag("errorBoundary.level", "app");
          scope.setTag("errorBoundary.name", "instant-quote-app");
          scope.setContext("applicationError", errorContext);

          if (user.isReady && user.userData) {
            scope.setUser({
              id: user.userData.id,
              email: user.userData.emailAddress || undefined,
            });
          }

          scope.setLevel("error");
          Sentry.captureException(error);
          resolve();
        });
      }),
      // Primary logger (includes Coralogix integration)
      new Promise<void>((resolve) => {
        Logger.error(
          "Application Error Boundary triggered",
          error,
          errorContext
        );
        resolve();
      }),
    ]).catch((loggingError) => {
      // Fallback: don't let logging errors crash the app
      Logger.error("Error logging failed", loggingError);
    });
  };

  const renderFallbackUI = () => {
    return (
      <div
        data-testid="error-panel"
        style={{
          height: "unset",
          textAlign: "center",
          placeItems: "center",
        }}
      >
        <Text kind="panel-title">Something bad happened!</Text>
        <Text kind="body">
          Please try closing this message and performing your most recent
          request again. You may also need to refresh your page and try again.
          If that still doesn&apos;t work, please contact{" "}
          <Link
            href="https://www.xometry.com/contact-us/"
            target="_blank"
            rel="noreferrer"
          >
            Xometry support
          </Link>
          .
        </Text>
      </div>
    );
  };

  return (
    <ErrorBoundary
      fallback={fallback ?? renderFallbackUI}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};

export default XomErrorBoundary;
